/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import ValidationRefundNotice from "@/components/ValidationRefundNotice";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, ShieldCheck, Lock, ArrowRight, Sparkles } from "lucide-react";
import Seo, { ldPricingCatalog, ldBreadcrumb } from "@/components/Seo";

const FEATURES = {
  standard: [
    "Profil référencé dans le réseau CONFILEX",
    "Réception des demandes d'accompagnement",
    "Messagerie sécurisée client",
    "Espace personnel spécialiste référencé",
  ],
  premium: [
    "Toutes les fonctionnalités Standard",
    "Profil mis en avant dans les propositions",
    "Coordonnées professionnelles enrichies",
    "Statistiques détaillées",
    "Priorité de réponse",
  ],
  institutionnel: [
    "Toutes les fonctionnalités Premium",
    "Référencement prioritaire en page d'accueil",
    "Apparition dans les sélections recommandées",
    "Accès aux dossiers stratégiques",
    "Accompagnement dédié CONFILEX",
    "Présence dans les communications institutionnelles",
  ],
};

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [packages, setPackages] = useState([]);
  const [submitting, setSubmitting] = useState(null);

  useEffect(() => {
    apiClient.get("/payments/packages").then(r => setPackages(r.data));
  }, []);

  const subscribe = async (tier) => {
    if (!user) { nav("/register"); return; }
    if (user.role !== "expert" && user.role !== "admin") {
      toast.error("Seuls les comptes spécialistes peuvent souscrire un référencement.");
      return;
    }
    setSubmitting(tier);
    try {
      const { data } = await apiClient.post("/payments/subscribe", {
        tier, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "L'initiation du règlement a échoué");
      setSubmitting(null);
    }
  };

  return (
    <div className="bg-[#FAFAF7] min-h-screen">
      <Seo
        title="Tarifs — Vérificateur 29/49/89 € TTC et référencement spécialiste"
        description="Tarifs CONFILEX : Vérificateur documentaire à partir de 29 € TTC (rapport simple, standard, approfondi avec mise en relation spécialiste). Référencement spécialiste à partir de 189 € HT par mois (Standard, Premium, Institutionnel)."
        canonical="/pricing"
        keywords={["tarifs confilex", "prix vérificateur document", "prix référencement avocat", "abonnement expert-comptable", "29 euros vérification", "49 euros standard", "89 euros approfondi"]}
        jsonLd={[
          ldPricingCatalog(),
          ldBreadcrumb([{ name: "Accueil", url: "/" }, { name: "Tarifs", url: "/pricing" }]),
        ]}
      />
      <Navbar variant="light" />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-4">Référencement spécialiste</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#0B3A2A] mb-5">
            Rejoignez le réseau au standard <span className="italic text-[#C9A961]">qui vous convient</span>.
          </h1>
          <p className="text-slate-600 leading-relaxed">
            Trois formules de référencement, conçues pour accompagner la visibilité et la qualité relationnelle de chaque cabinet ou spécialiste indépendant.
          </p>
        </div>

        {/* Mention obligatoire de transparence — validation préalable + remboursement intégral en cas de refus */}
        <ValidationRefundNotice className="max-w-3xl mx-auto mb-12" />

        <div className="grid md:grid-cols-3 gap-6">
          {packages.map((p) => {
            const tier = p.id;
            const isPremium = tier === "premium";
            const isElite = tier === "institutionnel";
            return (
              <Card key={tier} data-testid={`pricing-${tier}`}
                className={`relative p-8 rounded-2xl border bg-white transition-smooth ${
                  isElite ? "border-[#C9A961] shadow-md" : "border-slate-200"
                }`}>
                {isPremium && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs uppercase tracking-wider bg-[#0B3A2A] text-white px-3 py-1 rounded-full">
                    Recommandé
                  </div>
                )}
                {isElite && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs uppercase tracking-wider bg-[#C9A961] text-[#0B3A2A] px-3 py-1 rounded-full font-medium">
                    <Sparkles className="w-3 h-3 inline mr-1" /> Cercle institutionnel
                  </div>
                )}
                <h3 className="font-serif text-2xl text-[#0B3A2A] mb-2">{p.label}</h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="font-serif text-5xl text-[#0B3A2A]">{p.amount_eur.toFixed(0)}</span>
                  <span className="text-slate-500">€ / mois</span>
                </div>
                <ul className="space-y-3 mb-8 min-h-[200px]">
                  {(FEATURES[tier] || []).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-[#7A9B7E] mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => subscribe(tier)} disabled={submitting === tier}
                  data-testid={`subscribe-${tier}`}
                  className={`w-full rounded-full h-11 ${
                    isElite
                      ? "bg-[#C9A961] hover:bg-[#E3C77A] text-[#0B3A2A] font-semibold"
                      : "bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white"
                  }`}>
                  {submitting === tier ? "Redirection…" : "Souscrire"}
                  {submitting !== tier && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </Card>
            );
          })}
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-6 text-sm text-slate-600">
          <div className="flex gap-3">
            <ShieldCheck className="w-5 h-5 text-[#C9A961] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-[#0B3A2A] mb-1">Règlement sécurisé</div>
              Paiement protégé via Stripe. Aucune information bancaire n'est conservée par CONFILEX.
            </div>
          </div>
          <div className="flex gap-3">
            <Lock className="w-5 h-5 text-[#C9A961] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-[#0B3A2A] mb-1">Sans engagement</div>
              Renouvellement mensuel transparent. Vous conservez la maîtrise de votre référencement.
            </div>
          </div>
          <div className="flex gap-3">
            <Check className="w-5 h-5 text-[#C9A961] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-[#0B3A2A] mb-1">Facturation institutionnelle</div>
              Reçu de paiement et facture au format professionnel envoyés automatiquement.
            </div>
          </div>
        </div>

        <div className="mt-16 text-center">
          <Link to="/pricing" className="text-sm text-[#C9A961] hover:text-[#B8975A]">
            Vous n'êtes pas encore référencé ? Choisissez votre formule →
          </Link>
        </div>
      </section>

      {/* ============================================================
          VÉRIFICATEUR IA — 3 niveaux (29 / 39 / 49 € TTC)
          Restauré en v3.49 après régression signalée par l'utilisateur.
          Chaque niveau enrichit un dossier / analyse un document.
          ============================================================ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-4 pb-20" data-testid="pricing-verifier-section">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="text-xs uppercase tracking-[0.28em] text-[#C9A961] mb-4 font-semibold">
            Vérificateur IA · Analyse documentaire
          </div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#0B3A2A] mb-4">
            Faites vérifier vos documents <span className="italic text-[#C9A961]">avant toute décision</span>.
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Trois niveaux d'analyse documentaire par IA — chronologie, incohérences, pièces manquantes, questions à préparer. Facturé à l'unité, sans engagement.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {[
            { level: "essentiel", price: 29, name: "Analyse Essentielle",
              points: ["Résumé structuré du document","Faits clés & dates importantes","Incohérences apparentes","Pièces manquantes"],
              cta: "Démarrer une analyse 29 €" },
            { level: "avance", price: 39, name: "Analyse Approfondie", featured: true,
              points: ["Tout l'Analyse Essentielle","Chronologie automatique","Contradictions internes","Suggestions de documents complémentaires"],
              cta: "Démarrer une analyse 39 €" },
            { level: "premium", price: 49, name: "Analyse Premium précontentieuse",
              points: ["Tout l'Analyse Approfondie","Analyse multi-documents","Tableaux d'incohérences","Préparation transmission spécialiste"],
              cta: "Démarrer une analyse 49 €" },
          ].map((tier) => (
            <Card key={tier.level}
              className={`p-6 relative flex flex-col ${
                tier.featured
                  ? "border-2 border-[#C9A961] shadow-[0_18px_40px_-16px_rgba(201,169,97,0.4)]"
                  : "border border-slate-200"
              }`}
              data-testid={`verifier-card-${tier.level}`}>
              {tier.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A961] text-[#031B12] text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                  Le plus choisi
                </div>
              )}
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-serif text-4xl text-[#0B3A2A]">{tier.price}</span>
                <span className="text-lg text-[#0B3A2A]">€</span>
                <span className="text-xs text-slate-500 ml-1">TTC / analyse</span>
              </div>
              <div className="font-serif text-xl text-[#0B3A2A] mb-4">{tier.name}</div>
              <ul className="space-y-2 mb-6 flex-1">
                {tier.points.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-[#C9A961] flex-shrink-0 mt-0.5" /> {p}
                  </li>
                ))}
              </ul>
              <Button asChild
                className={tier.featured
                  ? "bg-[#C9A961] hover:bg-[#B8975A] text-[#031B12]"
                  : "bg-[#0B3A2A] hover:bg-[#062618] text-white"}>
                <Link to="/verifier" data-testid={`verifier-cta-${tier.level}`}>{tier.cta}</Link>
              </Button>
            </Card>
          ))}
        </div>

        <p className="text-center text-xs text-slate-500 mt-8 max-w-2xl mx-auto">
          Paiement Stripe sécurisé. Livraison immédiate du rapport. Aucun abonnement, aucun engagement. Le rapport ne constitue ni une consultation juridique ni un avis juridique, ni une consultation comptable, fiscale, notariale ou financière.
        </p>
      </section>

      {/* ============================================================
          MISE EN RELATION EXPERT NON ABONNÉ — 89 € TTC unique
          Restauré en v3.49. C'est le paiement unique qui déclenche
          la divulgation des coordonnées d'un cabinet non abonné et
          l'envoi automatique du dossier IA structuré.
          ============================================================ */}
      <section className="bg-[#F7F3EA] border-t border-b border-[#C9A961]/25">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16 lg:py-20" data-testid="pricing-outreach-section">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs uppercase tracking-[0.28em] text-[#C9A961] mb-4 font-semibold">
              Mise en relation ponctuelle
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#0B3A2A] mb-4">
              Contactez un cabinet non abonné pour <span className="italic text-[#C9A961]">89 € TTC</span>.
            </h2>
            <p className="text-slate-600 leading-relaxed">
              CONFILEX référence gratuitement des milliers de cabinets français. Pour contacter un cabinet <b>non abonné</b>, un frais unique de mise en relation débloque ses coordonnées et transmet automatiquement votre dossier IA structuré.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6 bg-white border border-[#C9A961]/40" data-testid="outreach-what">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-[#C9A961]" />
                <h3 className="font-serif text-lg text-[#0B3A2A]">Ce qui est inclus dans les 89 €</h3>
              </div>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#C9A961] flex-shrink-0 mt-0.5" /> Divulgation des coordonnées vérifiées du cabinet</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#C9A961] flex-shrink-0 mt-0.5" /> Envoi automatique de votre dossier IA structuré au cabinet</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#C9A961] flex-shrink-0 mt-0.5" /> Chronologie, pièces manquantes, questions préparées</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#C9A961] flex-shrink-0 mt-0.5" /> Notification de bonne réception</li>
              </ul>
            </Card>
            <Card className="p-6 bg-white border border-[#C9A961]/40" data-testid="outreach-when">
              <div className="flex items-center gap-2 mb-3">
                <Lock className="w-5 h-5 text-[#C9A961]" />
                <h3 className="font-serif text-lg text-[#0B3A2A]">Quand est-ce facturé ?</h3>
              </div>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#C9A961] flex-shrink-0 mt-0.5" /> <b>Après</b> validation de votre dossier IA (étapes 1→4 du parcours)</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#C9A961] flex-shrink-0 mt-0.5" /> <b>Avant</b> toute communication du dossier au cabinet</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#C9A961] flex-shrink-0 mt-0.5" /> Paiement Stripe unique — pas d'abonnement</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#C9A961] flex-shrink-0 mt-0.5" /> Si le cabinet ne répond pas sous 7 jours : réattribution gratuite</li>
              </ul>
            </Card>
          </div>

          <div className="text-center mt-10">
            <Button asChild className="bg-[#C9A961] hover:bg-[#B8975A] text-[#031B12] px-8">
              <Link to="/dossier" data-testid="outreach-cta">
                Démarrer mon dossier <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
            <p className="text-xs text-slate-500 mt-3">
              Les cabinets abonnés Confilex sont contactés gratuitement (aucun frais de mise en relation).
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
